import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'ar' | 'tl' | 'hi' | 'bn';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
}

const translations = {
  en: {
    // Navigation
    'nav.features': 'Features',
    'nav.about': 'About',
    'nav.pricing': 'Pricing',
    'nav.accessPlatform': 'Access Platform',
    
    // Hero Section
    'hero.title': 'Transform Your',
    'hero.titleHighlight': 'Garage Business',
    'hero.titleEnd': 'with Digital Excellence',
    'hero.subtitle': "Streamline operations, delight customers, and grow revenue with Kuwait's premier garage management platform.",
    'hero.getStarted': 'Get Started Now',
    'hero.watchDemo': 'Watch Demo',
    
    // Stats
    'stats.activeGarages': 'Active Garages',
    'stats.carsServiced': 'Cars Serviced',
    'stats.satisfaction': 'Customer Satisfaction',
    'stats.support': 'Support Available',
    
    // Features
    'features.title': 'Everything You Need to',
    'features.titleHighlight': 'Succeed',
    'features.subtitle': 'Comprehensive tools designed specifically for modern garage operations',
    'features.customerManagement': 'Customer Management',
    'features.customerManagementDesc': 'Keep detailed records of customers, their vehicles, service history, and preferences all in one place.',
    'features.jobTracking': 'Job Tracking',
    'features.jobTrackingDesc': 'Monitor repairs in real-time, assign tasks to technicians, and keep customers updated automatically.',
    'features.whatsapp': 'WhatsApp Integration',
    'features.whatsappDesc': "Send automated updates, payment links, and maintenance reminders directly to customers' WhatsApp.",
    'features.payments': 'Smart Payments',
    'features.paymentsDesc': 'Accept payments via KNET, Visa, and other local providers. Send payment links instantly.',
    'features.analytics': 'Analytics & Reports',
    'features.analyticsDesc': 'Track revenue, popular services, customer trends, and make data-driven decisions.',
    'features.security': 'Secure & Reliable',
    'features.securityDesc': 'Bank-grade security, automated backups, and 99.9% uptime guarantee for your peace of mind.',
    
    // About
    'about.title': 'Part of the',
    'about.titleHighlight': 'Slick Ecosystem',
    'about.description1': 'Rayyi7 is proudly part of the Slick Ecosystem, a comprehensive suite of digital solutions transforming businesses across Kuwait and the GCC region.',
    'about.description2': 'Owned and operated by',
    'about.slickCompany': 'Slick Company - Kuwait',
    'about.description3': ', we bring years of expertise in digital transformation to the automotive service industry.',
    'about.visitSlick': 'Visit Slick Ecosystem',
    'about.whyChoose': 'Why Choose Rayyi7?',
    'about.reason1': "Built specifically for Kuwait's automotive market",
    'about.reason2': 'Local payment methods (KNET, mada) integrated',
    'about.reason3': 'Arabic and English language support',
    'about.reason4': '24/7 local support team',
    
    // CTA
    'cta.title': 'Ready to Transform Your Garage Business?',
    'cta.subtitle': 'Join hundreds of successful garages already using Rayyi7',
    'cta.startTrial': 'Start Free Trial',
    'cta.contactSales': 'Contact Sales',
    
    // Footer
    'footer.product': 'Product',
    'footer.company': 'Company',
    'footer.support': 'Support',
    'footer.aboutUs': 'About Us',
    'footer.helpCenter': 'Help Center',
    'footer.documentation': 'Documentation',
    'footer.partOfSlick': 'Part of the Slick Ecosystem',
    'footer.ownedBy': 'Owned by Slick Company - Kuwait',
    'footer.copyright': '© 2025 Rayyi7 - Slick Company Kuwait. All rights reserved.',
    
    // Login Selection
    'login.welcomeTo': 'Welcome to Rayyi7',
    'login.selectAccess': 'Please select how you want to access the platform',
    'login.garageLogin': 'Garage Login',
    'login.garageDesc': 'For garage owners and employees to manage their operations',
    'login.garageMethod': 'Login with email and password',
    'login.carOwnerLogin': 'Car Owner Login',
    'login.carOwnerDesc': 'For customers to view their car maintenance details and history',
    'login.carOwnerMethod': 'Login with WhatsApp number',
    'login.carOwnerNote': 'Car owners: You will receive a WhatsApp message with login details to access your maintenance records',
    'login.backToHome': 'Back to Home',
    
    // Garage Login
    'garageLogin.title': 'Garage Login',
    'garageLogin.subtitle': 'Sign in to manage your garage',
    'garageLogin.email': 'Email Address',
    'garageLogin.password': 'Password',
    'garageLogin.rememberMe': 'Remember me',
    'garageLogin.forgotPassword': 'Forgot password?',
    'garageLogin.signIn': 'Sign In',
    'garageLogin.signingIn': 'Signing in...',
    'garageLogin.noAccount': "Don't have an account?",
    'garageLogin.register': 'Register your garage',
    'garageLogin.backToSelection': 'Back to Login Selection',
    
    // Customer Login
    'customerLogin.title': 'Car Owner Login',
    'customerLogin.subtitle': 'Access your car maintenance records',
    'customerLogin.phoneNumber': 'WhatsApp Phone Number',
    'customerLogin.phoneHint': 'Enter the phone number linked to your WhatsApp',
    'customerLogin.sendOTP': 'Send OTP via WhatsApp',
    'customerLogin.sendingOTP': 'Sending OTP...',
    'customerLogin.otpSent': "We've sent a 6-digit code to your WhatsApp",
    'customerLogin.enterOTP': 'Enter OTP Code',
    'customerLogin.verify': 'Verify & Login',
    'customerLogin.verifying': 'Verifying...',
    'customerLogin.changeNumber': 'Change phone number',
    'customerLogin.note': 'Note:',
    'customerLogin.noteText': 'You should have received a WhatsApp message from your garage with instructions to access your maintenance details.',
    
    // Pricing
    'pricing.title': 'Simple, Transparent Pricing',
    'pricing.subtitle': 'Choose the plan that fits your garage size and needs',
    'pricing.monthly': 'Monthly',
    'pricing.annually': 'Annually',
    'pricing.save': 'Save',
    'pricing.freeForCustomers': 'Always Free for Car Owners',
    'pricing.customerAccess': 'Car owners get free access to view their maintenance history, upcoming services, and communicate with their garage.',
    'pricing.perMonth': '/month',
    'pricing.perYear': '/year',
    'pricing.billedAnnually': 'billed annually',
    'pricing.mostPopular': 'Most Popular',
    'pricing.getStarted': 'Get Started',
    'pricing.contactUs': 'Contact Us',
    'pricing.features': 'Features',
    'pricing.customers': 'customers',
    'pricing.unlimited': 'Unlimited',
    'pricing.customersIncluded': 'customers included',
    'pricing.vehicleRecords': 'Vehicle records',
    'pricing.jobTracking': 'Job tracking & management',
    'pricing.whatsappNotifications': 'WhatsApp notifications',
    'pricing.paymentProcessing': 'Payment processing',
    'pricing.basicReports': 'Basic reports',
    'pricing.advancedAnalytics': 'Advanced analytics',
    'pricing.multiLocation': 'Multi-location support',
    'pricing.apiAccess': 'API access',
    'pricing.dedicatedSupport': 'Dedicated account manager',
    'pricing.customIntegrations': 'Custom integrations',
    'pricing.prioritySupport': 'Priority support',
    'pricing.teamTraining': 'Team training included',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.kwd': 'KWD',
    
    // Dashboard Common
    'dashboard': 'Dashboard',
    'welcome': 'Welcome',
    'logout': 'Logout',
    'settings': 'Settings',
    'reports': 'Reports',
    'quickActions': 'Quick Actions',
    'viewAll': 'View All',
    'status': 'Status',
    'amount': 'Amount',
    'date': 'Date',
    'actions': 'Actions',
    'edit': 'Edit',
    'delete': 'Delete',
    'save': 'Save',
    'cancel': 'Cancel',
    'search': 'Search',
    'filter': 'Filter',
    'export': 'Export',
    'print': 'Print',
    'refresh': 'Refresh',
    
    // Super Admin Dashboard
    'superAdminDashboard': 'Super Admin Dashboard',
    'platformOverview': 'Platform Overview',
    'totalGarages': 'Total Garages',
    'totalRevenue': 'Total Revenue',
    'activeUsers': 'Active Users',
    'monthlyGrowth': 'Monthly Growth',
    'revenueChart': 'Revenue Chart',
    'recentActivity': 'Recent Activity',
    'manageGarages': 'Manage Garages',
    'viewReports': 'View Reports',
    'systemSettings': 'System Settings',
    'garages': 'Garages',
    'users': 'Users',
    'subscriptions': 'Subscriptions',
    'activityType': 'Activity Type',
    'timestamp': 'Timestamp',
    
    // Garage Owner Dashboard
    'garageOwnerDashboard': 'Garage Owner Dashboard',
    'hereIsYourGarageOverview': 'Here\'s your garage overview for today',
    'activeJobs': 'Active Jobs',
    'todaysRevenue': 'Today\'s Revenue',
    'totalCustomers': 'Total Customers',
    'pendingInvoices': 'Pending Invoices',
    'createJobCard': 'Create Job Card',
    'addCustomer': 'Add Customer',
    'generateInvoice': 'Generate Invoice',
    'recentJobCards': 'Recent Job Cards',
    'jobId': 'Job ID',
    'customer': 'Customer',
    'vehicle': 'Vehicle',
    'employees': 'Employees',
    'customers': 'Customers',
    'vehicles': 'Vehicles',
    'jobCards': 'Job Cards',
    'inventory': 'Inventory',
    'invoices': 'Invoices',
    
    // Employee Dashboard
    'employeeDashboard': 'Employee Dashboard',
    'hereAreYourTasksForToday': 'Here are your tasks for today',
    'assignedTasks': 'Assigned Tasks',
    'completedToday': 'Completed Today',
    'inProgress': 'In Progress',
    'pendingTasks': 'Pending Tasks',
    'myTasks': 'My Tasks',
    'viewJobCards': 'View Job Cards',
    'startTask': 'Start Task',
    'checkInventory': 'Check Inventory',
    'myActiveTasks': 'My Active Tasks',
    'taskId': 'Task ID',
    'jobCard': 'Job Card',
    'task': 'Task',
    'priority': 'Priority',
    
    // Customer Dashboard
    'customerPortal': 'Customer Portal',
    'manageYourVehiclesAndServices': 'Manage your vehicles and service history',
    'bookAppointment': 'Book Appointment',
    'addVehicle': 'Add Vehicle',
    'payInvoice': 'Pay Invoice',
    'myVehicles': 'My Vehicles',
    'serviceHistory': 'Service History',
    'appointments': 'Appointments',
    'nextService': 'Next Service',
    'upcomingAppointments': 'Upcoming Appointments',
    'noUpcomingAppointments': 'No upcoming appointments',
    'recentServices': 'Recent Services',
    'serviceId': 'Service ID',
    'service': 'Service',
    
    // Vehicle Management
    'vehicleManagement': 'Vehicle Management',
    'searchVehicles': 'Search vehicles by plate, make, model, or VIN...',
    'noVehiclesFound': 'No vehicles found',
    'plateNumber': 'Plate Number',
    'makeModel': 'Make & Model',
    'make': 'Make',
    'model': 'Model',
    'year': 'Year',
    'owner': 'Owner',
    'mileage': 'Mileage',
    'editVehicle': 'Edit Vehicle',
    'updateVehicle': 'Update Vehicle',
    'confirmDeleteVehicle': 'Are you sure you want to delete this vehicle?',
    'customerID': 'Customer ID',
    'color': 'Color',
    'vin': 'VIN',
    'engineNumber': 'Engine Number',
    'fuelType': 'Fuel Type',
    'transmissionType': 'Transmission Type',
    'notes': 'Notes',
    'petrol': 'Petrol',
    'diesel': 'Diesel',
    'hybrid': 'Hybrid',
    'electric': 'Electric',
    'automatic': 'Automatic',
    'manual': 'Manual',
    'previous': 'Previous',
    'next': 'Next',
    
    // Customer Management
    'customerManagement': 'Customer Management',
    'searchCustomers': 'Search customers by name, phone, or email...',
    'noCustomersFound': 'No customers found',
    'editCustomer': 'Edit Customer',
    'updateCustomer': 'Update Customer',
    'customerDetails': 'Customer Details',
    'customerName': 'Customer Name',
    'contact': 'Contact',
    'customerSince': 'Customer Since',
    'lastVisit': 'Last Visit',
    'active': 'Active',
    'inactive': 'Inactive',
    'import': 'Import',
    'firstName': 'First Name',
    'lastName': 'Last Name',
    'fullName': 'Full Name',
    'phoneNumber': 'Phone Number',
    'phone': 'Phone',
    'email': 'Email',
    'optional': 'Optional',
    'preferredLanguage': 'Preferred Language',
    'arabic': 'Arabic',
    'english': 'English',
    'personalInformation': 'Personal Information',
    'statistics': 'Statistics',
    'services': 'Services',
    'name': 'Name',
    'language': 'Language',
    'close': 'Close',
    
    // Job Card Management
    'jobCardManagement': 'Job Card Management',
    'searchJobCards': 'Search job cards by number, service, or description...',
    'noJobCardsFound': 'No job cards found',
    'jobNumber': 'Job Number',
    'serviceType': 'Service Type',
    'assignedTo': 'Assigned To',
    'unassigned': 'Unassigned',
    'estimatedCost': 'Estimated Cost',
    'actualCost': 'Actual Cost',
    'mileageIn': 'Mileage In',
    'mileageOut': 'Mileage Out',
    'vehicleID': 'Vehicle ID',
    'filters': 'Filters',
    'allStatuses': 'All Statuses',
    'allPriorities': 'All Priorities',
    'pending': 'Pending',
    'completed': 'Completed',
    'cancelled': 'Cancelled',
    'low': 'Low',
    'medium': 'Medium',
    'high': 'High',
    'pendingJobs': 'Pending Jobs',
    'todaysJobs': "Today's Jobs",
    'monthRevenue': 'Month Revenue',
    'description': 'Description',
    
    // Invoices
    'invoices.title': 'Invoices',
    'invoices.subtitle': 'Manage and track customer invoices',
    'invoices.createNew': 'Create Invoice',
    'invoices.searchPlaceholder': 'Search by invoice number, customer...',
    'invoices.allStatuses': 'All Statuses',
    'invoices.draft': 'Draft',
    'invoices.sent': 'Sent',
    'invoices.paid': 'Paid',
    'invoices.overdue': 'Overdue',
    'invoices.cancelled': 'Cancelled',
    'invoices.noInvoices': 'No invoices found',
    'invoices.noInvoicesDesc': 'Get started by creating your first invoice',
    'invoices.invoiceNumber': 'Invoice #',
    'invoices.customer': 'Customer',
    'invoices.issueDate': 'Issue Date',
    'invoices.dueDate': 'Due Date',
    'invoices.amount': 'Amount',
    'invoices.status': 'Status',
    'invoices.send': 'Send',
    
    // Common actions and pagination
    'common.actions': 'Actions',
    'common.view': 'View',
    'common.edit': 'Edit',
    'common.download': 'Download',
    'common.showing': 'Showing',
    'common.to': 'to',
    'common.of': 'of',
    'common.results': 'results',
    'common.previous': 'Previous',
    'common.next': 'Next',
    
    // Customer Check-in
    'customerCheckIn': 'Customer Check-In',
    'searchOrRegisterCustomer': 'Search for existing customer or register a new one',
    'customerLookup': 'Customer Lookup',
    'pleaseEnterPhoneNumber': 'Please enter a phone number',
    'selectCountry': 'Select Country',
    'enterPhoneNumber': 'Enter phone number',
    'searching': 'Searching...',
    'searchCustomer': 'Search Customer',
    'searchCountries': 'Search countries...',
    'customerFound': 'Customer Found',
    'customerNotFound': 'Customer Not Found',
    'noCustomerWithThisPhone': 'No customer found with this phone number',
    'registerNewCustomer': 'Register New Customer',
    'newCustomerRegistration': 'New Customer Registration',
    'enterFullName': 'Enter full name',
    'enterEmail': 'customer@example.com',
    'enterAddress': 'Enter full address',
    'registerCustomer': 'Register Customer',
    'newSearch': 'New Search',
    'back': 'Back',
    'address': 'Address',
    'registeredVehicles': 'Registered Vehicles',
    'noVehiclesRegistered': 'No vehicles registered',
    'plate': 'Plate',
    'silver': 'Silver',
    'transmission': 'Transmission',
    'checkIn': 'Check-In',
    'customerCreatedSuccessfully': 'Customer created successfully',
    'vehicleAddedSuccessfully': 'Vehicle added successfully',
    'pleaseSelectCustomerFirst': 'Please select a customer first',
    'selectedCustomerVehicle': 'Selected Customer & Vehicle',
  },
  ar: {
    // Navigation
    'nav.features': 'المميزات',
    'nav.about': 'عن ريِّح',
    'nav.pricing': 'الأسعار',
    'nav.accessPlatform': 'دخول المنصة',
    
    // Hero Section
    'hero.title': 'حوّل',
    'hero.titleHighlight': 'أعمال الكراج',
    'hero.titleEnd': 'الخاصة بك إلى التميز الرقمي',
    'hero.subtitle': 'بسّط العمليات، أسعد العملاء، وزد الإيرادات مع منصة إدارة الكراجات الرائدة في الكويت.',
    'hero.getStarted': 'ابدأ الآن',
    'hero.watchDemo': 'شاهد العرض',
    
    // Stats
    'stats.activeGarages': 'كراج نشط',
    'stats.carsServiced': 'سيارة تم خدمتها',
    'stats.satisfaction': 'رضا العملاء',
    'stats.support': 'دعم متاح',
    
    // Features
    'features.title': 'كل ما تحتاجه',
    'features.titleHighlight': 'للنجاح',
    'features.subtitle': 'أدوات شاملة مصممة خصيصاً لعمليات الكراج الحديثة',
    'features.customerManagement': 'إدارة العملاء',
    'features.customerManagementDesc': 'احتفظ بسجلات مفصلة للعملاء وسياراتهم وتاريخ الخدمة وتفضيلاتهم في مكان واحد.',
    'features.jobTracking': 'تتبع الأعمال',
    'features.jobTrackingDesc': 'راقب الإصلاحات في الوقت الفعلي، ووزع المهام على الفنيين، وأبقِ العملاء على اطلاع تلقائياً.',
    'features.whatsapp': 'تكامل واتساب',
    'features.whatsappDesc': 'أرسل التحديثات التلقائية وروابط الدفع وتذكيرات الصيانة مباشرة إلى واتساب العملاء.',
    'features.payments': 'مدفوعات ذكية',
    'features.paymentsDesc': 'اقبل المدفوعات عبر كي نت وفيزا ومقدمي الخدمات المحليين الآخرين. أرسل روابط الدفع فوراً.',
    'features.analytics': 'التحليلات والتقارير',
    'features.analyticsDesc': 'تتبع الإيرادات والخدمات الشائعة واتجاهات العملاء واتخذ قرارات مبنية على البيانات.',
    'features.security': 'آمن وموثوق',
    'features.securityDesc': 'أمان بمستوى البنوك، نسخ احتياطية تلقائية، وضمان وقت تشغيل ٩٩.٩٪ لراحة بالك.',
    
    // About
    'about.title': 'جزء من',
    'about.titleHighlight': 'نظام سليك البيئي',
    'about.description1': 'ريِّح جزء فخور من نظام سليك البيئي، مجموعة شاملة من الحلول الرقمية التي تحول الأعمال في الكويت ومنطقة الخليج.',
    'about.description2': 'مملوكة ومدارة من قبل',
    'about.slickCompany': 'شركة سليك - الكويت',
    'about.description3': '، نجلب سنوات من الخبرة في التحول الرقمي لصناعة خدمات السيارات.',
    'about.visitSlick': 'زيارة نظام سليك البيئي',
    'about.whyChoose': 'لماذا تختار ريِّح؟',
    'about.reason1': 'مبنية خصيصاً لسوق السيارات الكويتي',
    'about.reason2': 'طرق دفع محلية (كي نت، مدى) مدمجة',
    'about.reason3': 'دعم اللغتين العربية والإنجليزية',
    'about.reason4': 'فريق دعم محلي ٢٤/٧',
    
    // CTA
    'cta.title': 'مستعد لتحويل أعمال الكراج الخاصة بك؟',
    'cta.subtitle': 'انضم إلى مئات الكراجات الناجحة التي تستخدم ريِّح بالفعل',
    'cta.startTrial': 'ابدأ التجربة المجانية',
    'cta.contactSales': 'تواصل مع المبيعات',
    
    // Footer
    'footer.product': 'المنتج',
    'footer.company': 'الشركة',
    'footer.support': 'الدعم',
    'footer.aboutUs': 'عن الشركة',
    'footer.helpCenter': 'مركز المساعدة',
    'footer.documentation': 'الوثائق',
    'footer.partOfSlick': 'جزء من نظام سليك البيئي',
    'footer.ownedBy': 'مملوكة لشركة سليك - الكويت',
    'footer.copyright': '© ٢٠٢٥ ريِّح - شركة سليك الكويت. جميع الحقوق محفوظة.',
    
    // Login Selection
    'login.welcomeTo': 'مرحباً بك في ريِّح',
    'login.selectAccess': 'يرجى اختيار كيفية الوصول إلى المنصة',
    'login.garageLogin': 'دخول الكراج',
    'login.garageDesc': 'لأصحاب الكراجات والموظفين لإدارة عملياتهم',
    'login.garageMethod': 'الدخول بالبريد الإلكتروني وكلمة المرور',
    'login.carOwnerLogin': 'دخول مالك السيارة',
    'login.carOwnerDesc': 'للعملاء لعرض تفاصيل صيانة سياراتهم وتاريخها',
    'login.carOwnerMethod': 'الدخول برقم الواتساب',
    'login.carOwnerNote': 'أصحاب السيارات: ستتلقى رسالة واتساب مع تفاصيل الدخول للوصول إلى سجلات الصيانة الخاصة بك',
    'login.backToHome': 'العودة للرئيسية',
    
    // Garage Login
    'garageLogin.title': 'دخول الكراج',
    'garageLogin.subtitle': 'سجل الدخول لإدارة الكراج الخاص بك',
    'garageLogin.email': 'البريد الإلكتروني',
    'garageLogin.password': 'كلمة المرور',
    'garageLogin.rememberMe': 'تذكرني',
    'garageLogin.forgotPassword': 'نسيت كلمة المرور؟',
    'garageLogin.signIn': 'تسجيل الدخول',
    'garageLogin.signingIn': 'جاري تسجيل الدخول...',
    'garageLogin.noAccount': 'ليس لديك حساب؟',
    'garageLogin.register': 'سجل الكراج الخاص بك',
    'garageLogin.backToSelection': 'العودة لاختيار الدخول',
    
    // Customer Login
    'customerLogin.title': 'دخول مالك السيارة',
    'customerLogin.subtitle': 'الوصول إلى سجلات صيانة سيارتك',
    'customerLogin.phoneNumber': 'رقم الواتساب',
    'customerLogin.phoneHint': 'أدخل رقم الهاتف المرتبط بالواتساب الخاص بك',
    'customerLogin.sendOTP': 'إرسال رمز التحقق عبر الواتساب',
    'customerLogin.sendingOTP': 'جاري إرسال رمز التحقق...',
    'customerLogin.otpSent': 'لقد أرسلنا رمز مكون من 6 أرقام إلى واتساب الخاص بك',
    'customerLogin.enterOTP': 'أدخل رمز التحقق',
    'customerLogin.verify': 'تحقق وسجل الدخول',
    'customerLogin.verifying': 'جاري التحقق...',
    'customerLogin.changeNumber': 'تغيير رقم الهاتف',
    'customerLogin.note': 'ملاحظة:',
    'customerLogin.noteText': 'يجب أن تكون قد تلقيت رسالة واتساب من الكراج الخاص بك مع تعليمات للوصول إلى تفاصيل الصيانة.',
    
    // Pricing
    'pricing.title': 'أسعار بسيطة وشفافة',
    'pricing.subtitle': 'اختر الخطة التي تناسب حجم واحتياجات الكراج الخاص بك',
    'pricing.monthly': 'شهري',
    'pricing.annually': 'سنوي',
    'pricing.save': 'وفر',
    'pricing.freeForCustomers': 'مجاني دائماً لأصحاب السيارات',
    'pricing.customerAccess': 'يحصل أصحاب السيارات على وصول مجاني لعرض تاريخ الصيانة والخدمات القادمة والتواصل مع الكراج.',
    'pricing.perMonth': '/شهر',
    'pricing.perYear': '/سنة',
    'pricing.billedAnnually': 'فاتورة سنوية',
    'pricing.mostPopular': 'الأكثر شعبية',
    'pricing.getStarted': 'ابدأ الآن',
    'pricing.contactUs': 'تواصل معنا',
    'pricing.features': 'المميزات',
    'pricing.customers': 'عملاء',
    'pricing.unlimited': 'غير محدود',
    'pricing.customersIncluded': 'عملاء مشمولين',
    'pricing.vehicleRecords': 'سجلات المركبات',
    'pricing.jobTracking': 'تتبع وإدارة الأعمال',
    'pricing.whatsappNotifications': 'إشعارات واتساب',
    'pricing.paymentProcessing': 'معالجة المدفوعات',
    'pricing.basicReports': 'تقارير أساسية',
    'pricing.advancedAnalytics': 'تحليلات متقدمة',
    'pricing.multiLocation': 'دعم مواقع متعددة',
    'pricing.apiAccess': 'وصول API',
    'pricing.dedicatedSupport': 'مدير حساب مخصص',
    'pricing.customIntegrations': 'تكاملات مخصصة',
    'pricing.prioritySupport': 'دعم ذو أولوية',
    'pricing.teamTraining': 'تدريب الفريق مشمول',
    
    // Common
    'common.loading': 'جاري التحميل...',
    'common.error': 'خطأ',
    'common.success': 'نجاح',
    'common.kwd': 'د.ك',
    
    // Dashboard Common
    'dashboard': 'لوحة التحكم',
    'welcome': 'مرحباً',
    'logout': 'تسجيل خروج',
    'settings': 'الإعدادات',
    'reports': 'التقارير',
    'quickActions': 'إجراءات سريعة',
    'viewAll': 'عرض الكل',
    'status': 'الحالة',
    'amount': 'المبلغ',
    'date': 'التاريخ',
    'actions': 'الإجراءات',
    'edit': 'تعديل',
    'delete': 'حذف',
    'save': 'حفظ',
    'cancel': 'إلغاء',
    'search': 'بحث',
    'filter': 'تصفية',
    'export': 'تصدير',
    'print': 'طباعة',
    'refresh': 'تحديث',
    
    // Super Admin Dashboard
    'superAdminDashboard': 'لوحة تحكم المدير العام',
    'platformOverview': 'نظرة عامة على المنصة',
    'totalGarages': 'إجمالي الكراجات',
    'totalRevenue': 'إجمالي الإيرادات',
    'activeUsers': 'المستخدمون النشطون',
    'monthlyGrowth': 'النمو الشهري',
    'revenueChart': 'مخطط الإيرادات',
    'recentActivity': 'النشاط الأخير',
    'manageGarages': 'إدارة الكراجات',
    'viewReports': 'عرض التقارير',
    'systemSettings': 'إعدادات النظام',
    'garages': 'الكراجات',
    'users': 'المستخدمون',
    'subscriptions': 'الاشتراكات',
    'activityType': 'نوع النشاط',
    'timestamp': 'الوقت',
    
    // Garage Owner Dashboard
    'garageOwnerDashboard': 'لوحة تحكم صاحب الكراج',
    'hereIsYourGarageOverview': 'إليك نظرة عامة على الكراج اليوم',
    'activeJobs': 'الأعمال النشطة',
    'todaysRevenue': 'إيرادات اليوم',
    'totalCustomers': 'إجمالي العملاء',
    'pendingInvoices': 'الفواتير المعلقة',
    'createJobCard': 'إنشاء بطاقة عمل',
    'addCustomer': 'إضافة عميل',
    'generateInvoice': 'إنشاء فاتورة',
    'recentJobCards': 'بطاقات العمل الأخيرة',
    'jobId': 'رقم العمل',
    'customer': 'العميل',
    'vehicle': 'المركبة',
    'employees': 'الموظفون',
    'customers': 'العملاء',
    'vehicles': 'المركبات',
    'jobCards': 'بطاقات العمل',
    'inventory': 'المخزون',
    'invoices': 'الفواتير',
    
    // Employee Dashboard
    'employeeDashboard': 'لوحة تحكم الموظف',
    'hereAreYourTasksForToday': 'إليك مهامك لليوم',
    'assignedTasks': 'المهام المخصصة',
    'completedToday': 'المكتملة اليوم',
    'inProgress': 'قيد التنفيذ',
    'pendingTasks': 'المهام المعلقة',
    'myTasks': 'مهامي',
    'viewJobCards': 'عرض بطاقات العمل',
    'startTask': 'بدء المهمة',
    'checkInventory': 'فحص المخزون',
    'myActiveTasks': 'مهامي النشطة',
    'taskId': 'رقم المهمة',
    'jobCard': 'بطاقة العمل',
    'task': 'المهمة',
    'priority': 'الأولوية',
    
    // Customer Dashboard
    'customerPortal': 'بوابة العميل',
    'manageYourVehiclesAndServices': 'إدارة مركباتك وتاريخ الخدمة',
    'bookAppointment': 'حجز موعد',
    'addVehicle': 'إضافة مركبة',
    'payInvoice': 'دفع فاتورة',
    'myVehicles': 'مركباتي',
    'serviceHistory': 'تاريخ الخدمة',
    'appointments': 'المواعيد',
    'nextService': 'الخدمة التالية',
    'upcomingAppointments': 'المواعيد القادمة',
    'noUpcomingAppointments': 'لا توجد مواعيد قادمة',
    'recentServices': 'الخدمات الأخيرة',
    'serviceId': 'رقم الخدمة',
    'service': 'الخدمة',
    
    // Vehicle Management
    'vehicleManagement': 'إدارة المركبات',
    'searchVehicles': 'البحث في المركبات بالوحة أو الماركة أو الموديل أو رقم الشاسيه...',
    'noVehiclesFound': 'لم يتم العثور على مركبات',
    'plateNumber': 'رقم اللوحة',
    'makeModel': 'الماركة والموديل',
    'make': 'الماركة',
    'model': 'الموديل',
    'year': 'السنة',
    'owner': 'المالك',
    'mileage': 'المسافة المقطوعة',
    'editVehicle': 'تعديل المركبة',
    'updateVehicle': 'تحديث المركبة',
    'confirmDeleteVehicle': 'هل أنت متأكد من حذف هذه المركبة؟',
    'customerID': 'رقم العميل',
    'color': 'اللون',
    'vin': 'رقم الشاسيه',
    'engineNumber': 'رقم المحرك',
    'fuelType': 'نوع الوقود',
    'transmissionType': 'نوع ناقل الحركة',
    'notes': 'ملاحظات',
    'petrol': 'بنزين',
    'diesel': 'ديزل',
    'hybrid': 'هايبرد',
    'electric': 'كهربائي',
    'automatic': 'أوتوماتيك',
    'manual': 'عادي',
    'previous': 'السابق',
    'next': 'التالي',
    
    // Customer Management
    'customerManagement': 'إدارة العملاء',
    'searchCustomers': 'البحث في العملاء بالاسم أو الهاتف أو البريد الإلكتروني...',
    'noCustomersFound': 'لم يتم العثور على عملاء',
    'editCustomer': 'تعديل العميل',
    'updateCustomer': 'تحديث العميل',
    'customerDetails': 'تفاصيل العميل',
    'customerName': 'اسم العميل',
    'contact': 'التواصل',
    'customerSince': 'عميل منذ',
    'lastVisit': 'آخر زيارة',
    'active': 'نشط',
    'inactive': 'غير نشط',
    'import': 'استيراد',
    'firstName': 'الاسم الأول',
    'lastName': 'اسم العائلة',
    'fullName': 'الاسم الكامل',
    'phoneNumber': 'رقم الهاتف',
    'phone': 'الهاتف',
    'email': 'البريد الإلكتروني',
    'optional': 'اختياري',
    'preferredLanguage': 'اللغة المفضلة',
    'arabic': 'العربية',
    'english': 'الإنجليزية',
    'personalInformation': 'المعلومات الشخصية',
    'statistics': 'الإحصائيات',
    'services': 'الخدمات',
    'name': 'الاسم',
    'language': 'اللغة',
    'close': 'إغلاق',
    
    // Job Card Management
    'jobCardManagement': 'إدارة بطاقات العمل',
    'searchJobCards': 'البحث في بطاقات العمل بالرقم أو الخدمة أو الوصف...',
    'noJobCardsFound': 'لم يتم العثور على بطاقات عمل',
    'jobNumber': 'رقم العمل',
    'serviceType': 'نوع الخدمة',
    'assignedTo': 'مخصص لـ',
    'unassigned': 'غير مخصص',
    'estimatedCost': 'التكلفة المقدرة',
    'actualCost': 'التكلفة الفعلية',
    'mileageIn': 'عداد المسافة عند الدخول',
    'mileageOut': 'عداد المسافة عند الخروج',
    'vehicleID': 'رقم المركبة',
    'filters': 'الفلاتر',
    'allStatuses': 'جميع الحالات',
    'allPriorities': 'جميع الأولويات',
    'pending': 'معلق',
    'completed': 'مكتمل',
    'cancelled': 'ملغي',
    'low': 'منخفض',
    'medium': 'متوسط',
    'high': 'عالي',
    'pendingJobs': 'الأعمال المعلقة',
    'todaysJobs': 'أعمال اليوم',
    'monthRevenue': 'إيرادات الشهر',
    'description': 'الوصف',
    
    // Invoices
    'invoices.title': 'الفواتير',
    'invoices.subtitle': 'إدارة وتتبع فواتير العملاء',
    'invoices.createNew': 'إنشاء فاتورة',
    'invoices.searchPlaceholder': 'البحث برقم الفاتورة أو العميل...',
    'invoices.allStatuses': 'جميع الحالات',
    'invoices.draft': 'مسودة',
    'invoices.sent': 'مُرسلة',
    'invoices.paid': 'مدفوعة',
    'invoices.overdue': 'متأخرة',
    'invoices.cancelled': 'ملغية',
    'invoices.noInvoices': 'لم يتم العثور على فواتير',
    'invoices.noInvoicesDesc': 'ابدأ بإنشاء فاتورتك الأولى',
    'invoices.invoiceNumber': 'رقم الفاتورة',
    'invoices.customer': 'العميل',
    'invoices.issueDate': 'تاريخ الإصدار',
    'invoices.dueDate': 'تاريخ الاستحقاق',
    'invoices.amount': 'المبلغ',
    'invoices.status': 'الحالة',
    'invoices.send': 'إرسال',
    
    // Common actions and pagination
    'common.actions': 'الإجراءات',
    'common.view': 'عرض',
    'common.edit': 'تعديل',
    'common.download': 'تحميل',
    'common.showing': 'عرض',
    'common.to': 'إلى',
    'common.of': 'من',
    'common.results': 'نتائج',
    'common.previous': 'السابق',
    'common.next': 'التالي',
    
    // Customer Check-in
    'customerCheckIn': 'تسجيل دخول العميل',
    'searchOrRegisterCustomer': 'البحث عن عميل موجود أو تسجيل عميل جديد',
    'customerLookup': 'البحث عن العميل',
    'pleaseEnterPhoneNumber': 'الرجاء إدخال رقم الهاتف',
    'selectCountry': 'اختر الدولة',
    'enterPhoneNumber': 'أدخل رقم الهاتف',
    'searching': 'جاري البحث...',
    'searchCustomer': 'البحث عن العميل',
    'searchCountries': 'البحث عن الدول...',
    'customerFound': 'تم العثور على العميل',
    'customerNotFound': 'لم يتم العثور على العميل',
    'noCustomerWithThisPhone': 'لم يتم العثور على عميل بهذا الرقم',
    'registerNewCustomer': 'تسجيل عميل جديد',
    'newCustomerRegistration': 'تسجيل عميل جديد',
    'enterFullName': 'أدخل الاسم الكامل',
    'enterEmail': 'customer@example.com',
    'enterAddress': 'أدخل العنوان الكامل',
    'registerCustomer': 'تسجيل العميل',
    'newSearch': 'بحث جديد',
    'back': 'رجوع',
    'address': 'العنوان',
    'registeredVehicles': 'المركبات المسجلة',
    'noVehiclesRegistered': 'لا توجد مركبات مسجلة',
    'plate': 'اللوحة',
    'silver': 'فضي',
    'transmission': 'ناقل الحركة',
    'checkIn': 'تسجيل الدخول',
    'customerCreatedSuccessfully': 'تم إنشاء العميل بنجاح',
    'vehicleAddedSuccessfully': 'تمت إضافة المركبة بنجاح',
    'pleaseSelectCustomerFirst': 'الرجاء اختيار عميل أولاً',
    'selectedCustomerVehicle': 'العميل والمركبة المختارة',
  },
  tl: { // Tagalog
    // Navigation
    'nav.features': 'Mga Feature',
    'nav.about': 'Tungkol',
    'nav.pricing': 'Presyo',
    'nav.accessPlatform': 'Access sa Platform',
    
    // Hero Section
    'hero.title': 'I-transform ang Inyong',
    'hero.titleHighlight': 'Garage Business',
    'hero.titleEnd': 'sa Digital Excellence',
    'hero.subtitle': 'Gawing simple ang operations, pasayahin ang customers, at palaguin ang revenue sa pinakamagandang garage management platform sa Kuwait.',
    'hero.getStarted': 'Magsimula Ngayon',
    'hero.watchDemo': 'Panoorin ang Demo',
    
    // Dashboard Common
    'dashboard': 'Dashboard',
    'welcome': 'Maligayang pagdating',
    'logout': 'Logout',
    'settings': 'Settings',
    'reports': 'Mga Report',
    'quickActions': 'Mabilis na Actions',
    'viewAll': 'Tingnan Lahat',
    'status': 'Status',
    'amount': 'Halaga',
    'date': 'Petsa',
    'actions': 'Mga Aksyon',
    'edit': 'I-edit',
    'delete': 'Tanggalin',
    'save': 'I-save',
    'cancel': 'Cancel',
    'search': 'Maghanap',
    'filter': 'Filter',
    'export': 'I-export',
    'print': 'I-print',
    'refresh': 'I-refresh',
    
    // Super Admin Dashboard
    'superAdminDashboard': 'Super Admin Dashboard',
    'platformOverview': 'Platform Overview',
    'totalGarages': 'Kabuuang Garages',
    'totalRevenue': 'Kabuuang Revenue',
    'activeUsers': 'Active na Users',
    'monthlyGrowth': 'Monthly Growth',
    'revenueChart': 'Revenue Chart',
    'recentActivity': 'Recent Activity',
    'manageGarages': 'Manage Garages',
    'viewReports': 'Tingnan ang Reports',
    'systemSettings': 'System Settings',
    'garages': 'Mga Garage',
    'users': 'Mga User',
    'subscriptions': 'Mga Subscription',
  },
  hi: { // Hindi
    // Navigation
    'nav.features': 'विशेषताएं',
    'nav.about': 'हमारे बारे में',
    'nav.pricing': 'मूल्य निर्धारण',
    'nav.accessPlatform': 'प्लेटफॉर्म एक्सेस',
    
    // Hero Section
    'hero.title': 'अपने',
    'hero.titleHighlight': 'गैराज बिजनेस',
    'hero.titleEnd': 'को डिजिटल एक्सीलेंस में बदलें',
    'hero.subtitle': 'कुवैत के प्रमुख गैराज प्रबंधन प्लेटफॉर्म के साथ संचालन को सुव्यवस्थित करें, ग्राहकों को खुश करें और राजस्व बढ़ाएं।',
    'hero.getStarted': 'अभी शुरू करें',
    'hero.watchDemo': 'डेमो देखें',
    
    // Dashboard Common
    'dashboard': 'डैशबोर्ड',
    'welcome': 'स्वागत है',
    'logout': 'लॉगआउट',
    'settings': 'सेटिंग्स',
    'reports': 'रिपोर्ट्स',
    'quickActions': 'त्वरित कार्य',
    'viewAll': 'सभी देखें',
    'status': 'स्थिति',
    'amount': 'राशि',
    'date': 'दिनांक',
    'actions': 'कार्य',
    'edit': 'संपादित करें',
    'delete': 'हटाएं',
    'save': 'सेव करें',
    'cancel': 'रद्द करें',
    'search': 'खोजें',
    'filter': 'फिल्टर',
    'export': 'निर्यात',
    'print': 'प्रिंट',
    'refresh': 'रीफ्रेश',
    
    // Super Admin Dashboard
    'superAdminDashboard': 'सुपर एडमिन डैशबोर्ड',
    'platformOverview': 'प्लेटफॉर्म ओवरव्यू',
    'totalGarages': 'कुल गैराज',
    'totalRevenue': 'कुल राजस्व',
    'activeUsers': 'सक्रिय उपयोगकर्ता',
    'monthlyGrowth': 'मासिक वृद्धि',
    'revenueChart': 'राजस्व चार्ट',
    'recentActivity': 'हाल की गतिविधि',
    'manageGarages': 'गैराज प्रबंधन',
    'viewReports': 'रिपोर्ट्स देखें',
    'systemSettings': 'सिस्टम सेटिंग्स',
    'garages': 'गैराज',
    'users': 'उपयोगकर्ता',
    'subscriptions': 'सब्सक्रिप्शन',
  },
  bn: { // Bengali
    // Navigation
    'nav.features': 'বৈশিষ্ট্য',
    'nav.about': 'আমাদের সম্পর্কে',
    'nav.pricing': 'মূল্য',
    'nav.accessPlatform': 'প্ল্যাটফর্ম অ্যাক্সেস',
    
    // Hero Section
    'hero.title': 'আপনার',
    'hero.titleHighlight': 'গ্যারেজ ব্যবসা',
    'hero.titleEnd': 'কে ডিজিটাল উৎকর্ষতায় রূপান্তরিত করুন',
    'hero.subtitle': 'কুয়েতের প্রধান গ্যারেজ ব্যবস্থাপনা প্ল্যাটফর্মের সাথে অপারেশন সুশৃঙ্খল করুন, গ্রাহকদের সন্তুষ্ট করুন এবং আয় বৃদ্ধি করুন।',
    'hero.getStarted': 'এখনই শুরু করুন',
    'hero.watchDemo': 'ডেমো দেখুন',
    
    // Dashboard Common
    'dashboard': 'ড্যাশবোর্ড',
    'welcome': 'স্বাগতম',
    'logout': 'লগআউট',
    'settings': 'সেটিংস',
    'reports': 'রিপোর্ট',
    'quickActions': 'দ্রুত কার্যক্রম',
    'viewAll': 'সব দেখুন',
    'status': 'অবস্থা',
    'amount': 'পরিমাণ',
    'date': 'তারিখ',
    'actions': 'কার্যক্রম',
    'edit': 'সম্পাদনা',
    'delete': 'মুছুন',
    'save': 'সংরক্ষণ',
    'cancel': 'বাতিল',
    'search': 'অনুসন্ধান',
    'filter': 'ফিল্টার',
    'export': 'রপ্তানি',
    'print': 'প্রিন্ট',
    'refresh': 'রিফ্রেশ',
    
    // Super Admin Dashboard
    'superAdminDashboard': 'সুপার অ্যাডমিন ড্যাশবোর্ড',
    'platformOverview': 'প্ল্যাটফর্ম ওভারভিউ',
    'totalGarages': 'মোট গ্যারেজ',
    'totalRevenue': 'মোট আয়',
    'activeUsers': 'সক্রিয় ব্যবহারকারী',
    'monthlyGrowth': 'মাসিক বৃদ্ধি',
    'revenueChart': 'আয়ের চার্ট',
    'recentActivity': 'সাম্প্রতিক কার্যকলাপ',
    'manageGarages': 'গ্যারেজ ব্যবস্থাপনা',
    'viewReports': 'রিপোর্ট দেখুন',
    'systemSettings': 'সিস্টেম সেটিংস',
    'garages': 'গ্যারেজসমূহ',
    'users': 'ব্যবহারকারীগণ',
    'subscriptions': 'সাবস্ক্রিপশন',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    // Arabic, Hindi, and Bengali can use RTL, but for this implementation we'll keep Arabic RTL only
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    const languageTranslations = translations[language] as any;
    return languageTranslations[key] || translations['en'][key as keyof typeof translations['en']] || key;
  };

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};